// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  engineApiV2,
  EngineTagTypes,
} from '@shared/data-access/api-base/citec-engine-api';
import { rawApi } from '@shared/data-access/api/engine/raw-api';
import { currencyApi } from '@shared/data-access/api/brokerage/currency-api';
import { illiquidAssetsSlice } from '@shared/data-access/slices/illiquid-assets/illiquid-assets-slice';
import {
  convertIlliquidAssetCurrency,
  convertIlliquidAssetsCurrency,
} from '@shared/data-access/transformers/illiquids/convert-illiquid-asset-currency';
import { getExchangeRates } from '@shared/data-access/transformers/currency/get-exchange-rates';
import { SharedTagTypes } from '@shared/data-access/utils/constants';
import type {
  CreateIlliquidAssetEventParams,
  CreateIlliquidAssetMutationParams,
  DeleteIlliquidAssetEventParams,
  DeleteIlliquidAssetParams,
  IlliquidAsset,
  IlliquidAssetEvent,
  IlliquidAssetEventResponse,
  IlliquidAssetResponse,
  IlliquidAssetsList,
  ListIlliquidAssetsQueryParams,
  UpdateIlliquidAssetMutationParams,
} from '@shared/data-access/types/engine-types';
import { selectClientIlliquidAssetsData } from '@shared/data-access/slices/illiquid-assets/illiquid-assets-selectors';

export const illiquidsApi = engineApiV2.injectEndpoints({
  endpoints: build => ({
    listIlliquidAssets: build.query<
      IlliquidAssetsList,
      ListIlliquidAssetsQueryParams
    >({
      queryFn: async (params, api) => {
        const { currency: accountCurrency, ...engineParams } = params;

        const result = await api.dispatch(
          rawApi.endpoints.listIlliquidAssetsRaw.initiate(engineParams, {
            forceRefetch: api.forced,
          }),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data ?? { assets: [] };

        const currencies = new Set(response.assets.map(asset => asset.currency));

        const getPrices = (currencies: string[]) =>
          api.dispatch(currencyApi.endpoints.currencyPrices.initiate(currencies));

        const exchangeRates = await getExchangeRates(
          Array.from(currencies),
          accountCurrency,
          getPrices,
        );

        const assets = convertIlliquidAssetsCurrency(
          response.assets,
          exchangeRates,
          accountCurrency,
        );

        return { data: { assets } };
      },
      onQueryStarted: async (arg, api) => {
        try {
          const { data } = await api.queryFulfilled;

          api.dispatch(
            illiquidAssetsSlice.actions.setClientIlliquidAssets({
              id: arg.client_id ?? 0,
              assets: data.assets,
            }),
          );
        } catch (error) {
          console.error('[listIlliquidAssets]', error);
        }
      },
      providesTags: result =>
        result?.assets?.length
          ? [
              ...result.assets.map(asset => ({
                type: EngineTagTypes.IlliquidAsset,
                id: asset.id,
              })),
              { type: EngineTagTypes.IlliquidAsset, id: 'LIST' },
              SharedTagTypes.CurrencyConversion,
            ]
          : [
              { type: EngineTagTypes.IlliquidAsset, id: 'LIST' },
              SharedTagTypes.CurrencyConversion,
            ],
    }),

    createIlliquidAsset: build.mutation<
      IlliquidAsset,
      CreateIlliquidAssetMutationParams
    >({
      queryFn: async (arg, api) => {
        const { currency: accountCurrency, body, client_id } = arg;

        const result = await api.dispatch(
          rawApi.endpoints.createIlliquidAssetRaw.initiate({ body, client_id }),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data as IlliquidAssetResponse;

        const getPrices = (currencies: string[]) =>
          api.dispatch(currencyApi.endpoints.currencyPrices.initiate(currencies));

        const exchangeRates = await getExchangeRates(
          [response.currency],
          accountCurrency,
          getPrices,
        );

        const data = convertIlliquidAssetCurrency(
          response,
          exchangeRates,
          accountCurrency,
        );

        return { data };
      },
      onQueryStarted: async (arg, api) => {
        try {
          const { data } = await api.queryFulfilled;

          api.dispatch(
            illiquidAssetsSlice.actions.addIlliquidAsset({
              id: arg.client_id ?? 0,
              asset: data,
            }),
          );
        } catch (error) {
          console.error('[createIlliquidAsset]', error);
        }
      },
      invalidatesTags: (result, error) => {
        if (error || !result) {
          return [];
        }

        return [{ type: EngineTagTypes.IlliquidAsset, id: 'LIST' }];
      },
    }),

    deleteIlliquidAsset: build.mutation<void, DeleteIlliquidAssetParams>({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.deleteIlliquidAssetRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: null as unknown as void };
      },
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;

          api.dispatch(
            illiquidAssetsSlice.actions.removeIlliquidAssets({
              id: arg.client_id ?? 0,
              assetId: arg.asset_id,
            }),
          );
        } catch (error) {
          console.error('[deleteIlliquidAsset]', error);
        }
      },
      invalidatesTags: (result, error, arg) => {
        if (error) {
          return [];
        }

        return [
          { type: EngineTagTypes.IlliquidAsset, id: arg.asset_id },
          { type: EngineTagTypes.IlliquidAsset, id: 'LIST' },
        ];
      },
    }),

    updateIlliquidAsset: build.mutation<
      IlliquidAsset,
      UpdateIlliquidAssetMutationParams
    >({
      queryFn: async (arg, api) => {
        const { currency: accountCurrency, ...params } = arg;

        const result = await api.dispatch(
          rawApi.endpoints.updateIlliquidAssetRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        const response = result.data as IlliquidAssetResponse;

        const getPrices = (currencies: string[]) =>
          api.dispatch(currencyApi.endpoints.currencyPrices.initiate(currencies));

        const exchangeRates = await getExchangeRates(
          [response.currency],
          accountCurrency,
          getPrices,
        );

        return {
          data: convertIlliquidAssetCurrency(
            response,
            exchangeRates,
            accountCurrency,
          ),
        };
      },
      onQueryStarted: async (arg, api) => {
        const clientId = arg.client_id ?? 0;

        const previous = selectClientIlliquidAssetsData(
          api.getState() as any,
          clientId,
        ).find(asset => asset.id === arg.asset_id);

        if (previous && arg.body.ibans) {
          api.dispatch(
            illiquidAssetsSlice.actions.updateIlliquidAsset({
              id: clientId,
              asset: { ...previous, ibans: arg.body.ibans },
            }),
          );
        }

        try {
          const { data } = await api.queryFulfilled;

          const merged: IlliquidAsset = previous
            ? {
                ...previous,
                ...data,
                events: data.events ?? previous.events,
                ibans: data.ibans ?? previous.ibans,
              }
            : data;

          api.dispatch(
            illiquidAssetsSlice.actions.updateIlliquidAsset({
              id: clientId,
              asset: merged,
            }),
          );
        } catch (error) {
          console.error('[updateIlliquidAsset]', error);

          if (previous) {
            api.dispatch(
              illiquidAssetsSlice.actions.updateIlliquidAsset({
                id: clientId,
                asset: previous,
              }),
            );
          }
        }
      },
      // Link/unlink only patches ibans; slice optimistic update is enough — avoid
      // list refetch that flashes the page Loader (`isFetching` → status fetching).
      invalidatesTags: () => [],
    }),

    createIlliquidAssetEvent: build.mutation<
      IlliquidAssetEvent,
      CreateIlliquidAssetEventParams
    >({
      queryFn: async (arg, api) => {
        const { body, asset_id, client_id } = arg;

        const result = await api.dispatch(
          rawApi.endpoints.createIlliquidAssetEventRaw.initiate({
            asset_id,
            body,
            client_id,
          }),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: result.data as IlliquidAssetEventResponse };
      },
      invalidatesTags: (result, error, arg) => {
        if (error || !result) {
          return [];
        }

        return [
          { type: EngineTagTypes.IlliquidAsset, id: arg.asset_id },
          { type: EngineTagTypes.IlliquidAsset, id: 'LIST' },
        ];
      },
    }),

    deleteIlliquidAssetEvent: build.mutation<void, DeleteIlliquidAssetEventParams>({
      queryFn: async (params, api) => {
        const result = await api.dispatch(
          rawApi.endpoints.deleteIlliquidAssetEventRaw.initiate(params),
        );

        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }

        return { data: null as unknown as void };
      },
      onQueryStarted: async (arg, api) => {
        const clientId = arg.client_id ?? 0;

        const previous = selectClientIlliquidAssetsData(
          api.getState() as any,
          clientId,
        ).find(asset => asset.id === arg.asset_id);

        api.dispatch(
          illiquidAssetsSlice.actions.removeIlliquidAssetEvent({
            id: clientId,
            assetId: arg.asset_id,
            eventId: arg.event_id,
          }),
        );

        try {
          await api.queryFulfilled;
        } catch (error) {
          console.error('[deleteIlliquidAssetEvent]', error);

          if (previous) {
            api.dispatch(
              illiquidAssetsSlice.actions.updateIlliquidAsset({
                id: clientId,
                asset: previous,
              }),
            );
          }
        }
      },
      invalidatesTags: (result, error, arg) => {
        if (error) {
          return [];
        }

        return [
          { type: EngineTagTypes.IlliquidAsset, id: arg.asset_id },
          { type: EngineTagTypes.IlliquidAsset, id: 'LIST' },
        ];
      },
    }),
  }),
});
